import {Decoder} from 'json-bouncer'
import * as R from 'ramda'
import {OrderFilter} from '../../Components/OrderFiltering/Model/OrderFilter'
import * as OrderFilteringState
    from '../../Components/OrderFiltering/Model/OrderFilteringState'
import {OrderFilteringFeedback} from '../../Components/OrderFiltering/OrderFilteringFeedback'
import {
    orderFilterHttpResponseAction,
    orderFilteringReducer,
} from '../../Components/OrderFiltering/OrderFilteringReducer'
import {indexedMap} from '../../Data/ArrayHelper'
import * as ClientSidePagination from '../../Data/ClientSidePagination'
import {FeedbackLoop} from '../../Data/FeedbackLoop'
import * as Downloadable from '../../Data/Downloadable'
import {decodeHttpResponse, HttpError} from '../../Data/Http'
import {HttpRequest} from '../../Data/HttpRequest'
import {update} from '../../Data/ObjectHelper'
import * as OptionalWebData from '../../Data/OptionalWebData'
import * as OrderExportOptions from '../../Data/OrderExportOptions'
import {
    DetailedPreviewOrderRow,
    OrderExportPreview
} from '../../Data/OrderExportPreview'
import * as OrderExportSelection from '../../Data/OrderExportSelection'
import {Paginated, paginatedDecoder} from '../../Data/Paginated'
import * as Result from '../../Data/Result'
import * as SerializedOrderFilters from '../../Data/SerializedOrderFilters'
import {Session} from '../../Data/Session'
import {OrderExport, orderExportDecoder} from '../../Entities/OrderExport'
import {
    OrderWithOwners,
    orderWithOwnersDecoder
} from '../../Entities/OrderWithOwners'
import {
    OrderWithOwnersAndPositions,
    orderWithOwnersAndPositionsDecoder
} from '../../Entities/OrderWithOwnersAndPositions'
import * as OrderExportResource from '../../Request/OrderExportResource'
import * as OrderResource from '../../Request/OrderResource'
import {OrderExportCreateAction} from './OrderExportCreateActions'
import {
    OrderExportCreateDownloadRequestIdentifier,
    OrderExportCreateFeedback,
    OrderExportCreateRequestIdentifier
} from './OrderExportCreateFeedback'
import {LastExport, OrderExportCreateState} from './OrderExportCreateState'

export const exportOffsetLimit = {offset: 0, limit: 2000}

export function activate(orderExportTemplateId: number): HttpRequest {
    return OrderExportResource.retrieve(orderExportTemplateId)
}

export function initialDecoder(): Decoder<OrderExport> {
    return orderExportDecoder()
}

export function initWithTemplate(orderExport: OrderExport): OrderExportCreateState {
    return initContent(orderExport.filters, orderExport.options)
}

export function initWithoutTemplate(): OrderExportCreateState {
    return initContent([], OrderExportOptions.all())
}

function initContent(
    orderFilters: OrderFilter[],
    options: OrderExportOptions.OrderExportOptions,
): OrderExportCreateState {
    return {
        orderFilter: OrderFilteringState.init(orderFilters),
        options,
        exportPreview: {type: 'NotAsked'},
        itemsPerPage: 25,
        loadingPreview: false,
        inputChanged: false,
        lastExport: {type: 'NotAsked'}
    }
}

function initCompactPreview(
    {data, totalCount}: Paginated<OrderWithOwners>,
): OrderExportPreview {
    return {
        type: 'CompactPreview',
        orderRows: {
            items:
                R.map(
                    orderWithOwners => ({
                        orderWithOwners,
                        selected: true,
                    }),
                    data,
                ),
            totalRemoteCount: totalCount,
            currentPage: 1,
        }
    }
}

function initDetailedPreview(
    {data, totalCount}: Paginated<OrderWithOwnersAndPositions>,
): OrderExportPreview {
    return {
        type: 'DetailedPreview',
        orderRows: {
            items: R.map(
                ({order, bpsUser, customer, company, positions}) => ({
                    orderWithOwners: {order, bpsUser, customer, company},
                    selected: true,
                    positionRows:
                        R.map(
                            position => ({
                                position,
                                selected: true,
                            }),
                            positions,
                        ),
                }),
                data,
            ),
            totalRemoteCount: totalCount,
            currentPage: 1,
        },
    }
}

type OrderExportCreateLoop =
    FeedbackLoop<OrderExportCreateState, OrderExportCreateFeedback>

export function reduce(
    session: Session,
    state: OrderExportCreateState,
    action: OrderExportCreateAction,
): OrderExportCreateLoop {
    switch (action.type) {
        case 'MenuButtonClicked':
            return {
                state,
                feedback: [{type: 'OpenDrawer'}],
            }
        case 'OrderFilteringModuleAction':
            const
                orderFilterLoop =
                    orderFilteringReducer(
                        state.orderFilter,
                        action.subAction,
                    ),
                wrappedLoop = {
                    state: update(
                        {orderFilter: orderFilterLoop.state},
                        state,
                    ),
                    feedback: [],
                }
            return R.reduce(
                (
                    previousLoop: FeedbackLoop<OrderExportCreateState, OrderExportCreateFeedback>,
                    feedback: OrderFilteringFeedback
                ): FeedbackLoop<OrderExportCreateState, OrderExportCreateFeedback> => {
                    switch (feedback.type) {
                        case 'SendRequest':
                            return {
                                state: previousLoop.state,
                                feedback:
                                    R.append<OrderExportCreateFeedback>(
                                        {
                                            type: 'SendRequest',
                                            identifier: {
                                                type: 'OrderFilterRequest',
                                                identifier: feedback.identifier,
                                            },
                                            httpRequest: feedback.httpRequest,
                                        },
                                        previousLoop.feedback,
                                    ),
                            }
                        case 'FiltersChanged':
                            return {
                                state:
                                    update(
                                        {inputChanged: true},
                                        previousLoop.state,
                                    ),
                                feedback: previousLoop.feedback,
                            }
                    }
                },
                wrappedLoop,
                orderFilterLoop.feedback,
            )
        case 'OrdersWithOwnersListResponse':
            return {
                state: update(
                    {
                        loadingPreview: false,
                        exportPreview:
                            OptionalWebData.fromResult(
                                Result.map(
                                    (paginatedOrders): OrderExportPreview =>
                                        initCompactPreview(paginatedOrders),
                                    decodeHttpResponse(
                                        paginatedDecoder(
                                            orderWithOwnersDecoder(),
                                        ),
                                        action.result,
                                    ),
                                ),
                            ),
                    },
                    state,
                ),
                feedback: [],
            }
        case 'OrdersWithOwnersAndPositionsListResponse':
            return {
                state: update(
                    {
                        loadingPreview: false,
                        exportPreview:
                            OptionalWebData.fromResult(
                                Result.map(
                                    (paginatedOrders): OrderExportPreview =>
                                        initDetailedPreview(paginatedOrders),
                                    decodeHttpResponse(
                                        paginatedDecoder(
                                            orderWithOwnersAndPositionsDecoder(),
                                        ),
                                        action.result,
                                    ),
                                ),
                            ),
                    },
                    state,
                ),
                feedback: [],
            }
        case 'OptionChanged':
            return {
                state:
                    update(
                        {
                            inputChanged: true,
                            options:
                                action.checked
                                    ? state.options.add(action.option)
                                    : state.options.delete(action.option)
                        },
                        state,
                    ),
                feedback: [],
            }
        case 'FetchPreview':
            const serializedOrderFilters =
                SerializedOrderFilters.fromOrderFilters(
                    state.orderFilter.activeFilters
                )
            return {
                state:
                    update(
                        {loadingPreview: true, inputChanged: false},
                        state,
                    ),
                feedback:
                    [
                        OrderExportOptions.needsPositions(state.options) ?
                            {
                                type: 'SendRequest',
                                identifier:
                                    {type: 'OrdersWithOwnersAndPositionsListRequest'},
                                httpRequest:
                                    OrderResource.listWithOwnersAndPositions(
                                        session.manufacturer.id,
                                        serializedOrderFilters,
                                        exportOffsetLimit,
                                    ),
                            } :
                            {
                                type: 'SendRequest',
                                identifier: {type: 'OrdersWithOwnersListRequest'},
                                httpRequest:
                                    OrderResource.listWithOwners(
                                        session.manufacturer.id,
                                        serializedOrderFilters,
                                        exportOffsetLimit,
                                    ),
                            },
                    ],
            }
        case 'CompactPreviewSelectionChanged':
            return {
                state: update(
                    {
                        exportPreview:
                            OptionalWebData.map(
                                exportPreview =>
                                    exportPreview.type === 'CompactPreview' ?
                                        update(
                                            {
                                                orderRows: update(
                                                    {
                                                        items:
                                                            indexedMap(
                                                                (
                                                                    index,
                                                                    orderRow,
                                                                ) => update(
                                                                    {
                                                                        selected:
                                                                        action.selected === 'all' ||
                                                                        R.contains(index, action.selected)
                                                                    },
                                                                    orderRow,
                                                                ),
                                                                exportPreview.orderRows.items,
                                                            )
                                                    },
                                                    exportPreview.orderRows,
                                                ),
                                            },
                                            exportPreview,
                                        ) :
                                        exportPreview
                                ,
                                state.exportPreview,
                            ),
                    },
                    state,
                ),
                feedback:
                    [],
            }
        case 'DetailedPreviewOrderSelectedChanged':
            return {
                state:
                    updateDetailedExportOrderRow(
                        orderRow =>
                            update(
                                {
                                    selected: action.checked,
                                    positionRows:
                                        R.map(
                                            positionRow => update(
                                                {selected: action.checked},
                                                positionRow
                                            ),
                                            orderRow.positionRows,
                                        ),
                                },
                                orderRow,
                            ),
                        action.id,
                        state,
                    ),
                feedback: [],
            }
        case 'DetailedPreviewPositionSelectedChanged':
            return {
                state: updateDetailedExportOrderRow(
                    orderRow =>
                        update(
                            {
                                selected: orderRow.selected || action.checked,
                                positionRows:
                                    R.map(
                                        positionRow =>
                                            positionRow.position.id === action.id ?
                                                update({selected: action.checked}, positionRow) :
                                                positionRow,
                                        orderRow.positionRows,
                                    ),
                            },
                            orderRow,
                        ),
                    action.orderId,
                    state,
                ),
                feedback: [],
            }
        case 'CreateExport':
            if (!state.loadingPreview &&
                !state.inputChanged &&
                state.exportPreview.type === 'Success' &&
                state.lastExport.type !== 'Loading'
            ) {
                return {
                    state: update(
                        {lastExport: {type: 'Loading'}},
                        state,
                    ),
                    feedback: [{
                        type: 'SendRequest',
                        identifier: {type: 'OrderExportCreateRequest'},
                        httpRequest:
                            OrderExportResource.create(
                                session.manufacturer.id,
                                SerializedOrderFilters.fromOrderFilters(
                                    state.orderFilter.activeFilters,
                                ),
                                state.options,
                                OrderExportSelection.fromExportPreview(
                                    state.exportPreview.value,
                                ),
                            ),
                    }],
                }
            } else {
                return {state: state, feedback: []}
            }
        case 'OrderExportCreateResponse': {
            const result =
                decodeHttpResponse(orderExportDecoder(), action.result)
            return {
                state: update(
                    {
                        lastExport:
                            OptionalWebData.map(
                                (orderExport): LastExport => ({
                                    orderExport,
                                    download: {type: 'Loading'}
                                }),
                                OptionalWebData.fromResult(result),
                            ),
                    },
                    state,
                ),
                feedback:
                    result.type === 'Ok' ?
                        [requestOrderExportDownload(result.value.id)] :
                        [],
            }
        }
        case 'OrderExportDownloadResponse':
            return {
                state:
                    update(
                        {
                            lastExport:
                                OptionalWebData.map(
                                    lastExport => update(
                                        {
                                            download:
                                                Downloadable.fromResult(
                                                    action.result,
                                                )
                                        },
                                        lastExport
                                    ),
                                    state.lastExport
                                ),
                        },
                        state,
                    ),
                feedback: [],
            }
        case 'DownloadOrderExport': {
            if (state.lastExport.type === 'Success') {
                return {
                    state: update(
                        {
                            lastExport:
                                OptionalWebData.map(
                                    lastExport => update(
                                        {download: {type: 'Loading'}},
                                        lastExport,
                                    ),
                                    state.lastExport,
                                )
                        },
                        state,
                    ),
                    feedback: [
                        requestOrderExportDownload(
                            state.lastExport.value.orderExport.id,
                        ),
                    ],
                }
            } else {
                return {state: state, feedback: []}
            }
        }
        case 'GoToPreviousPage':
            return {
                state: update(
                    {
                        exportPreview:
                            OptionalWebData.map(
                                (preview: OrderExportPreview): OrderExportPreview =>
                                    update(
                                        {
                                            orderRows:
                                                ClientSidePagination.previous(
                                                    preview.orderRows,
                                                ),
                                        },
                                        preview,
                                    ),
                                state.exportPreview,
                            )
                    },
                    state,
                ),
                feedback: [],
            }
        case 'GoToNextPage':
            return {
                state: update(
                    {
                        exportPreview:
                            OptionalWebData.map(
                                (preview: OrderExportPreview): OrderExportPreview =>
                                    update(
                                        {
                                            orderRows:
                                                ClientSidePagination.next(
                                                    state.itemsPerPage,
                                                    preview.orderRows,
                                                ),
                                        },
                                        preview,
                                    ),
                                state.exportPreview,
                            )
                    },
                    state,
                ),
                feedback: [],
            }
    }
}

function requestOrderExportDownload(id: number): OrderExportCreateFeedback {
    return {
        type: 'SendDownloadRequest',
        identifier: {type: 'OrderExportDownloadRequest'},
        httpRequest: OrderExportResource.download(id),
        filename: 'Export.zip',
    }
}

function updateDetailedExportOrderRow(
    fn: (orderRow: DetailedPreviewOrderRow) => DetailedPreviewOrderRow,
    id: number,
    state: OrderExportCreateState
): OrderExportCreateState {
    return update(
        {
            exportPreview:
                OptionalWebData.map(
                    exportPreview =>
                        exportPreview.type === 'DetailedPreview' ?
                            update(
                                {
                                    orderRows:
                                        update(
                                            {
                                                items:
                                                    R.map(
                                                        orderRow =>
                                                            orderRow.orderWithOwners.order.id === id ?
                                                                fn(orderRow) :
                                                                orderRow,
                                                        exportPreview.orderRows.items,
                                                    ),

                                            },
                                            exportPreview.orderRows,
                                        ),
                                },
                                exportPreview,
                            ) :
                            exportPreview,
                    state.exportPreview,
                ),
        },
        state,
    )
}

export function httpResponseAction(
    identifier: OrderExportCreateRequestIdentifier,
    result: Result.Result<HttpError, string>,
): OrderExportCreateAction {
    switch (identifier.type) {
        case 'OrderFilterRequest':
            return {
                type: 'OrderFilteringModuleAction',
                subAction:
                    orderFilterHttpResponseAction(
                        identifier.identifier,
                        result,
                    ),
            }
        case 'OrdersWithOwnersListRequest':
            return {
                type: 'OrdersWithOwnersListResponse',
                result,
            }
        case 'OrdersWithOwnersAndPositionsListRequest':
            return {
                type: 'OrdersWithOwnersAndPositionsListResponse',
                result,
            }
        case 'OrderExportCreateRequest':
            return {
                type: 'OrderExportCreateResponse',
                result,
            }
    }
}

export function httpDownloadResponseAction(
    identifier: OrderExportCreateDownloadRequestIdentifier,
    result: Result.Result<HttpError, null>,
): OrderExportCreateAction {
    switch (identifier.type) {
        case 'OrderExportDownloadRequest':
            return {
                type: 'OrderExportDownloadResponse',
                result,
            }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Pages/OrderExportCreate/OrderExportCreateReducer.ts